import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ArrayAtLeastOne, CatalogueCode, Category, ChildCode, Code, CodeCategory, CodeFinance, CodeImportRequest, CodeImportResponse, ItemDeletedResponse, PagingOptions } from '@jms/model';
import { catchError, lastValueFrom, map, Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CodeService {


    private codeAPI = '/api/codes/';

    constructor(
        private http: HttpClient,
    ) { }

    public loadCategories$(): Observable<Category[]> {

        const categories = this.http.get<Category[]>(this.codeAPI + 'categories')
            .pipe(
                map(x => x),
                catchError(httpError => {

                    return of([]);
                })
            );

        return categories;
    }


    public async searchCode(filter: string): Promise<CatalogueCode[]> {

        const request = {
            filter: filter,
            itemOnly: false
        } as any;
        return await lastValueFrom(this.http.post<Code[]>(`${this.codeAPI}filter/all`, request).pipe(
            map(x => x),
            catchError(httpError => {

                return of([]);
            })
        ));

    }

    public async loadBySubCategory(subCategoryId: string, paging: PagingOptions): Promise<CatalogueCode[]> {

        const request = {
            category: subCategoryId,
            paging: paging,
        } as any;

        const codes = await lastValueFrom(this.http.post<Code[]>(`${this.codeAPI}category/all`, request)
            .pipe(
                map(x => x),
                catchError(httpError => {

                    return of([]);
                })
            ));

        return codes;
    }

    public async loadCodeById(codeId: string): Promise<CatalogueCode> {

        const code = await lastValueFrom(this.http.get<Code>(`${this.codeAPI}${codeId}`)
            .pipe(
                map(x => x),
                catchError(httpError => {

                    return of(null);
                })
            ))

        return code;
    }

    public async addCode(request: Code): Promise<CatalogueCode> {

        const code = await lastValueFrom(this.http.post<CatalogueCode>(this.codeAPI, request)
            .pipe(
                map(x => x),
                catchError(httpError => {

                    return of(null);
                })
            ))

        return code;

    }

    public async pachCode(request: Partial<CatalogueCode>): Promise<CatalogueCode> {

        const code = await lastValueFrom(this.http.patch<CatalogueCode>(`${this.codeAPI}`, request)
            .pipe(
                map(x => x),
                catchError(httpError => {

                    return of(null);
                })
            ))

        return code;

    }

    public async addChildCodes(request: Partial<CatalogueCode>): Promise<CatalogueCode> {

        const code = await lastValueFrom(this.http.patch<CatalogueCode>(`${this.codeAPI}child`, request)
            .pipe(
                map(x => x),
                catchError(httpError => {

                    return of(null);
                })
            ))

        return code;

    }

    public async patchChildCode(parentId: string, child: ChildCode): Promise<ChildCode> {

        const code = await lastValueFrom(this.http.patch<ChildCode>(`${this.codeAPI}child/${parentId}`, child)
            .pipe(
                map(x => x),
                catchError(httpError => {

                    return of(null);
                })
            ))

        return code;

    }

    public async deleteChildCode(parentId: string, childId: string): Promise<ItemDeletedResponse> {

        const code = await lastValueFrom(this.http.delete<ItemDeletedResponse>(`${this.codeAPI}${parentId}/${childId}`)
            .pipe(
                map(x => x),
                catchError(httpError => {

                    return of({ _id: childId, success: false });
                })
            ))

        return code;

    }



    public async importCodeToSystem(request: CodeImportRequest[]): Promise<CodeImportResponse[]> {

        const codes = await lastValueFrom(this.http.post<CodeImportResponse[]>(this.codeAPI + 'importcodestosystem', request)
            .pipe(
                map(codes => codes),
                catchError(httpError => {

                    return of([]);
                })
            ));

        return codes;
    }

    public getFinanceInfo(request: string[]): Observable<CodeFinance[]> {
        const response = this.http.post<any[]>(this.codeAPI + 'finance', request)
            .pipe(
                // map(x => x),
                catchError(httpError => {
                    return of([]);
                })
            );

        return response;
    }

    public getByChildCodeName(name: string): Observable<CatalogueCode[]> {
        const response = this.http.get<CatalogueCode[]>(this.codeAPI + 'child/' + encodeURIComponent(name))
            .pipe(
                // map(x => x),
                catchError(httpError => {
                    return of([]);
                })
            );

        return response;
    }

    // Fetch selected catalogue fields by code name (keyed on name, not _id, since embedded codes track a separate catalogueCodeId)
    public getInfosByName(codeName: string, props: ArrayAtLeastOne<keyof CatalogueCode>): Observable<Partial<CatalogueCode>> {
        if (!codeName) {
            return of(null);
        }

        const response = this.http.get<Partial<CatalogueCode>>(this.codeAPI + encodeURIComponent(codeName) + '/infos/' + props.join(';'))
            .pipe(
                catchError(httpError => {
                    return of(null);
                })
            );

        return response;
    }

}